/**
 * Records a version of the 206 whenever one of its three tables changes.
 *
 * Boxes 4 to 6 are aid stations, ambulance services and hospitals, each its own
 * entity with its own mutation hooks, and each save should leave a draft
 * version behind the way the 202's boxes do. Rather than thread a callback down
 * through the shared table, the card provides the recorder here and the three
 * wrappers pick it up.
 *
 * The default is a no-op so those tables keep working outside the card, where
 * there is no operational period to record against.
 */
import React, { createContext, useContext, useMemo } from 'react';

import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

const RecordMedicalPlanVersionContext = createContext<() => void>(() => {});

export const useRecordMedicalPlanVersion = (): (() => void) =>
  useContext(RecordMedicalPlanVersionContext);

export const MedicalPlanVersionRecording: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(
    'ICS_206',
    effectivePeriodNumber
  );

  const record = useMemo(
    () => () => {
      if (!readOnlyForms) recordVersion.mutate();
    },
    [readOnlyForms, recordVersion]
  );

  return (
    <RecordMedicalPlanVersionContext.Provider value={record}>
      {children}
    </RecordMedicalPlanVersionContext.Provider>
  );
};

export default MedicalPlanVersionRecording;
